import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";

import { useDrive } from "../hooks/useDrive";
import { useUpload } from "../hooks/useUpload";
import { useTree } from "../hooks/useTree";

import Breadcrumbs from "../components/Breadcrumbs";
import DirectoryTree from "../components/DirectoryTree";
import FileGrid from "../components/FileGrid";
import UploadToast from "../components/UploadToast";
import NewFolderModal from "../components/NewFolderModal";
import RenameModal from "../components/RenameModal";
import MoveModal from "../components/MoveModal";
import ContextMenu from "../components/ContextMenu";
import ShareLinkModal from "../components/ShareLinkModal";
import BackgroundContextMenu from "../components/BackgroundContextMenu";
import DeleteModal from "../components/DeleteModal";
import OverwriteModal from "../components/OverwriteModal";
import UploadModal from "../components/UploadModal";
import ErrorModal from "../components/ErrorModal";

import type { DriveFile, DriveFolder } from "../types";
import * as api from "../api/storageApi";
import { invalidateListCache } from "../hooks/useDrive";

// ─── Helpers para calcular prefijos afectados por mutaciones ────────────
const parentPrefix = (p: string): string => {
  const norm = String(p ?? "").replace(/^\/+/, "").replace(/\/+$/, "");
  const i = norm.lastIndexOf("/");
  return i >= 0 ? norm.slice(0, i) : "";
};

const uniquePaths = (paths: string[]): string[] =>
  Array.from(new Set(paths.map(p => p.replace(/\/+$/, ""))));

// ─── View mode persistence ────────────────────────────────────────
const VIEW_MODE_KEY = "drive:view-mode";

type CtxState = {
  x: number;
  y: number;
  item: DriveFile | DriveFolder;
  type: "file" | "folder";
};

type BgCtxState = {
  x: number;
  y: number;
};

// ─── Loading con timeout visual ──────────────────────────────────
function LoadingIndicator({ onRetry }: { onRetry: () => void }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t0 = Date.now();
    const interval = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="d-flex align-items-center justify-content-center h-100">
      <div className="text-center">
        <div className="spinner-border mb-3" style={{ color: "#2563eb" }} />
        <p style={{ color: "#64748b" }}>Cargando…</p>
        {elapsed >= 3 && (
          <div className="mt-2">
            <small className="d-block mb-2" style={{ color: "#94a3b8" }}>
              {elapsed}s — tardando más de lo normal
            </small>
            <button className="btn btn-sm btn-feebbo-outline" onClick={onRetry}>
              <i className="bi bi-arrow-clockwise me-1" />
              Reintentar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DrivePage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthenticator((ctx) => [ctx.user]);

  const userLabel = user?.signInDetails?.loginId ?? user?.username ?? "";
  const userInitial = (userLabel.trim()[0] ?? "?").toUpperCase();

  // Estado de verificación de acceso
  const [accessCheck, setAccessCheck] = useState<{ loading: boolean; hasAccess: boolean; email: string; reason?: string }>({ loading: true, hasAccess: false, email: '', reason: '' });

  // Verificar acceso al montar el componente
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await api.hasUserAccess();
        setAccessCheck({ loading: false, ...result });
      } catch (err) {
        console.error('Error checking access:', err);
        setAccessCheck({ loading: false, hasAccess: false, email: '', reason: 'Error al verificar permisos' });
      }
    };
    checkAccess();
  }, []);

  const handleSignOut = async () => {
    try {
      if (typeof api.clearRequestQueue === 'function') {
        api.clearRequestQueue();
      }
    } catch {}
    signOut();
  };

  // ── Drive state ─────────────────────────────────────────────────
  const drive = useDrive();
  const upload = useUpload(drive.prefix, () => {
    invalidateListCache(drive.prefix);
    drive.load(drive.prefix, { silent: true, force: true });
  }, (errorMsg: string) => {
    // Detectar si es un mensaje de sanitización (advertencia) o un error real
    const isSanitizationWarning = errorMsg.includes('Se han sanitizado');
    setErrorModal({
      title: isSanitizationWarning ? 'Nombres de archivo corregidos' : 'Error en la subida',
      message: errorMsg,
      variant: isSanitizationWarning ? 'warning' : 'error'
    });
  });
  const tree = useTree(drive.prefix);


  // ── UI state ────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return saved === "list" || saved === "grid" ? saved : "list";
  });
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderPrefix, setNewFolderPrefix] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const isResizingRef = useRef(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DriveFile | DriveFolder | null>(null);
  const [shareTarget, setShareTarget] = useState<DriveFile | null>(null);
  const [ctx, setCtx] = useState<CtxState | null>(null);
  const [bgCtx, setBgCtx] = useState<BgCtxState | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [deleteItems, setDeleteItems] = useState<(DriveFile | DriveFolder)[]>([]);
  const [moveItems, setMoveItems] = useState<(DriveFile | DriveFolder)[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [downloadingContextZip, setDownloadingContextZip] = useState(false);

  // Move/mover toast state
  const [movingItems, setMovingItems] = useState(false);
  const [moveProgress, setMoveProgress] = useState(0);
  const [movePhase, setMovePhase] = useState('');
  
  // Error modal state
  const [errorModal, setErrorModal] = useState<{title: string; message: string; variant?: 'error' | 'warning' | 'info'} | null>(null);
  
  // Filtros avanzados
  const [filterType, setFilterType] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');

  // ── Refs ─────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dragCountRef = useRef(0);
  
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);
  
  // ── Keyboard shortcuts ───────────────────────────────────────────
  useEffect(() => {
    const navigateToParent = () => {
      if (!drive.prefix) return; // Ya estamos en la raíz
      
      // Calcular el directorio padre
      const parts = drive.prefix.replace(/\/$/, '').split('/').filter(Boolean);
      parts.pop();
      const parentPath = parts.length ? parts.join('/') : '';
      
      // Verificar que no intentemos navegar más atrás del basePath del usuario
      const normalizedBasePath = drive.basePath === '*' ? '' : drive.basePath;
      if (normalizedBasePath) {
        // Si el parentPath es más corto que basePath, no permitir navegación
        if (parentPath.length < normalizedBasePath.length) {
          console.log('⚠️ No se puede navegar más atrás del directorio base del usuario');
          return;
        }
      }
      
      // Navegar (esto guarda en localStorage y dispara load automáticamente)
      drive.navigate(parentPath);
      
      // Invalidar caché para forzar recarga desde servidor
      invalidateListCache(parentPath);
      
      // Refrescar el tree para sincronizar con la nueva ruta
      tree.refresh({ paths: [parentPath] });
    };

    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "u") {
        e.preventDefault();
        fileInputRef.current?.click();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        drive.selectAll();
      }
      if (e.key === "Delete" && drive.selected.size > 0) drive.deleteSelected();
      if (e.key === "Escape") {
        drive.clearSelection();
        drive.setSearch("");
      }
      
      // Navegar al directorio padre (cd ..)
      // Soporta: PageUp, Alt+Left, Backspace (fuera de inputs)
      if (e.key === "PageUp" || e.key === "Prior") {
        e.preventDefault();
        navigateToParent();
      }
      if (e.key === "Backspace" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigateToParent();
      }
      if (e.key === "ArrowLeft" && e.altKey) {
        e.preventDefault();
        navigateToParent();
      }
    };

    // Manejar botones del ratón (botón "atrás" del ratón)
    const mouseHandler = (e: MouseEvent) => {
      // Botón 3 = botón "atrás" del ratón
      if (e.button === 3) {
        e.preventDefault();
        navigateToParent();
      }
    };

    window.addEventListener("keydown", handler);
    window.addEventListener("mousedown", mouseHandler);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("mousedown", mouseHandler);
    };
  }, [drive]);

  // ── After mutations ──────────────────────────────────────────────
  // affectedPaths: prefijos que han cambiado (origen, destino, padres).
  //                Si se pasan, hacemos refresh quirúrgico + silent reload.
  //                Si no, refresh completo + spinner (modo legacy / botón refresh).
  const afterMutation = useCallback((affectedPaths?: string[]) => {
    if (affectedPaths && affectedPaths.length > 0) {
      const targets = uniquePaths(affectedPaths);
      // Invalidar caché de los prefijos afectados
      invalidateListCache(...targets);
      // Recargar el listado actual sin spinner (SWR mostrará lo cacheado mientras revalida)
      drive.load(drive.prefix, { silent: true, force: true });
      // Refrescar solo las ramas afectadas del árbol
      tree.refresh({ paths: targets });
    } else {
      // Modo legacy: refresh completo (ej. botón refrescar manual)
      invalidateListCache();
      drive.load(drive.prefix, { force: true });
      tree.refresh();
    }
  }, [drive, tree]);

  // ── Move centralizado con toast ────────────────────────────────
  const computeMoveAffectedPaths = (items: api.MoveItem[]): string[] => {
    const out: string[] = [];
    for (const it of items) {
      out.push(parentPrefix(it.path));
      out.push(parentPrefix(it.destination));
    }
    return out;
  };

  const executeMoveWithToast = useCallback(async (items: api.MoveItem[]) => {
    setMovingItems(true);
    setMoveProgress(0);
    setMovePhase('iniciando');

    // Cierra toast + dispara refresh INMEDIATAMENTE (sin esperas artificiales)
    const finishSuccess = () => {
      setMovingItems(false);
      setMoveProgress(0);
      setMovePhase('');
      afterMutation(computeMoveAffectedPaths(items));
    };

    try {
      const result = await api.moveFiles(items);

      // Si es un job asíncrono, hacer polling
      if (result.jobId) {
        const job = await api.pollJob(result.jobId, (pct, phase) => {
          setMoveProgress(pct);
          if (phase) setMovePhase(phase);
        });

        if (job.status === 'completed') {
          finishSuccess();
        } else if (job.status === 'completed_with_errors') {
          finishSuccess();
          if (job.errorDetails && job.errorDetails.length > 0) {
            const errorList = job.errorDetails
              .map(e => `• ${e.source}\n  ${e.error}`)
              .join('\n\n');
            setErrorModal({
              title: `Movido con ${job.errorDetails.length} error(es)`,
              message: `Se movieron algunos archivos pero hubo errores:\n\n${errorList}`,
            });
          }
        } else if (job.status === 'failed') {
          setMovePhase('error');
          console.error('❌ Move job failed:', job);

          let errorMessage = 'La operación de mover archivos falló.';
          if (job.errorDetails && job.errorDetails.length > 0) {
            const errorList = job.errorDetails
              .map(e => `• ${e.source}\n  ${e.error}`)
              .join('\n\n');
            errorMessage = `Errores encontrados:\n\n${errorList}`;
          }
          setErrorModal({ title: 'Error al mover', message: errorMessage });

          // Mantener visible 2s para que el usuario vea el estado de error
          await new Promise(r => setTimeout(r, 2000));
          setMovingItems(false);
          setMoveProgress(0);
          setMovePhase('');
        }
      } else {
        // Síncrono - completado inmediatamente
        if (result.errors && result.errors.length > 0) {
          console.warn('Move errors:', result.errors);
        }
        finishSuccess();
      }
    } catch (err) {
      setMovePhase('error');
      console.error('❌ Move error:', err);
      setErrorModal({
        title: 'Error al mover archivos',
        message: err instanceof Error ? err.message : String(err),
      });
      await new Promise(r => setTimeout(r, 2000));
      setMovingItems(false);
      setMoveProgress(0);
      setMovePhase('');
    }
  }, [afterMutation]);

  const handleRenameDone = useCallback(() => {
    // El rename mantiene la misma carpeta padre que el item
    const parent = renameTarget ? parentPrefix(renameTarget.path) : drive.prefix;
    setRenameTarget(null);
    afterMutation([parent]);
  }, [afterMutation, renameTarget, drive.prefix]);

  // Si todavía está verificando acceso, mostrar loading
  if (accessCheck.loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" style={{ color: "#2563eb" }} />
          <p style={{ color: "#64748b" }}>Verificando permisos...</p>
        </div>
      </div>
    );
  }
  
  // Si no tiene acceso, mostrar panel bloqueante
  if (!accessCheck.hasAccess) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh', backgroundColor: '#f8fafc' }}>
        <div className="text-center" style={{ maxWidth: '500px', padding: '2rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🔒</div>
          <h2 style={{ color: '#1e293b', marginBottom: '1rem' }}>Acceso Denegado</h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            {accessCheck.reason || 'No tienes permisos para acceder a esta aplicación.'}
          </p>
          {accessCheck.email && (
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Usuario: <strong>{accessCheck.email}</strong>
            </p>
          )}
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '2rem' }}>
            Si crees que esto es un error, contacta con el administrador del sistema.
          </p>
          <button 
            className="btn btn-primary"
            onClick={handleSignOut}
            style={{ backgroundColor: '#2563eb', border: 'none', padding: '0.5rem 1.5rem' }}
          >
            <i className="bi bi-box-arrow-right me-2" />
            Cerrar Sesión
          </button>
        </div>
      </div>
    );
  }

  // ── Drag & drop (upload desde fuera) ─────────────────────────────

  const isExternalDrag = (e: React.DragEvent): boolean => {
    const types = Array.from(e.dataTransfer.types);
    // Si contiene application/json, es un drag interno (move de items)
    if (types.includes('application/json')) return false;
    // Si contiene Files o items, es un drag externo (upload)
    return types.includes('Files') || types.includes('Items');
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isExternalDrag(e)) return;
    setDragOver(true);
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isExternalDrag(e)) return;
    dragCountRef.current++;
    if (dragCountRef.current === 1) setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isExternalDrag(e)) return;
    dragCountRef.current--;
    if (dragCountRef.current <= 0) {
      dragCountRef.current = 0;
      setDragOver(false);
    }
  };
  
  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    dragCountRef.current = 0;
    
    // Si es un drag interno (move de items), ignorar - lo maneja FileGrid
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('application/json')) {
      return; // Es drag interno, no upload
    }
    
    const items = e.dataTransfer.items;
    if (!items) {
      // Fallback para navegadores antiguos
      const files = Array.from(e.dataTransfer.files);
      if (files.length) upload.upload(files);
      return;
    }
    
    // Procesar items con soporte para carpetas
    const files: File[] = [];
    const promises: Promise<void>[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.() || (item as any).getAsEntry?.();
        if (entry) {
          promises.push(traverseFileTree(entry, '', files));
        } else {
          // Fallback si no hay soporte para FileSystemEntry
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    }
    
    await Promise.all(promises);
    if (files.length) upload.upload(files);
  };
  
  // Helper para recorrer el árbol de archivos/carpetas
  const traverseFileTree = async (entry: any, path: string, files: File[]): Promise<void> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file: File) => {
          // Crear un nuevo File con webkitRelativePath para mantener la estructura
          const newFile = new File([file], file.name, { type: file.type });
          Object.defineProperty(newFile, 'webkitRelativePath', {
            value: path + file.name,
            writable: false
          });
          files.push(newFile);
          resolve();
        });
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        dirReader.readEntries(async (entries: any[]) => {
          const subPromises = entries.map(subEntry => 
            traverseFileTree(subEntry, path + entry.name + '/', files)
          );
          await Promise.all(subPromises);
          resolve();
        });
      } else {
        resolve();
      }
    });
  };

  // ── Context menu actions ─────────────────────────────────────────
  const handleDownloadZip = async (path: string) => {
    setDownloadingContextZip(true);
    try {
      const { jobId } = await api.downloadAsZip(path);
      
      const job = await api.pollJob(jobId, () => {
        // Progreso del ZIP
      });

      if ((job.status === 'completed' || job.status === 'completed_with_errors') && job.downloadURL) {
        const a = document.createElement('a');
        a.href = job.downloadURL;
        a.download = 'download.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Mostrar errores si hubo problemas
        if (job.status === 'completed_with_errors' && job.errorDetails && job.errorDetails.length > 0) {
          const errorList = job.errorDetails
            .map(e => `• ${e.source}\n  ${e.error}`)
            .join('\n\n');
          setErrorModal({
            title: `ZIP descargado con ${job.errorDetails.length} error(es)`,
            message: `El ZIP se descargó pero algunos archivos tuvieron errores:\n\n${errorList}`
          });
        }
        
        await new Promise(resolve => setTimeout(resolve, 3000));
      } else {
        console.error('❌ ZIP falló:', job.status, job.errorDetails);
        
        let errorMessage = 'No se pudo generar el archivo ZIP.';
        if (job.errorDetails && job.errorDetails.length > 0) {
          const errorList = job.errorDetails
            .map(e => `• ${e.source}\n  ${e.error}`)
            .join('\n\n');
          errorMessage = `Errores al generar ZIP:\n\n${errorList}`;
        }
        
        setErrorModal({
          title: 'Error al descargar ZIP',
          message: errorMessage
        });
      }
    } catch (e) {
      console.error("❌ Error downloading ZIP:", e);
      setErrorModal({
        title: 'Error al descargar ZIP',
        message: e instanceof Error ? e.message : String(e)
      });
    } finally {
      setDownloadingContextZip(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);

    try {
      const paths = deleteItems.map(item => item.path);
      const affected = paths.map(parentPrefix);

      await api.deleteFiles(paths);

      setDeleteItems([]);
      setDeleting(false);
      drive.clearSelection();
      afterMutation(affected);
    } catch (err) {
      setDeleting(false);
      console.error("❌ Delete error:", err);
      setErrorModal({
        title: 'Error al eliminar',
        message: err instanceof Error ? err.message : String(err)
      });
    }
  };

  const handleMoveConfirm = async (destinationPath: string) => {
    const items = moveItems.map(item => {
      const isFolder = item.path.endsWith('/') || !('size' in item);
      const sourcePath = item.path.replace(/^\/+/, '').replace(/\/$/, '');
      const fileName = item.name;
      const destDir = destinationPath ? destinationPath.replace(/^\/+/, '').replace(/\/$/, '') : '';
      const destPath = destDir ? `${destDir}/${fileName}` : fileName;
      return {
        type: (isFolder ? 'folder' : 'file') as 'file' | 'folder',
        path: isFolder ? `${sourcePath}/` : sourcePath,
        destination: isFolder ? `${destPath}/` : destPath
      };
    });

    setMoveItems([]);
    drive.clearSelection();
    executeMoveWithToast(items);
  };

  // const handleMoveSelected = () => {
  //   const selectedItems = [
  //     ...drive.allFolders.filter(f => drive.selected.has(f.path)),
  //     ...drive.allFiles.filter(f => drive.selected.has(f.path))
  //   ];
  //   if (selectedItems.length > 0) {
  //     setMoveItems(selectedItems);
  //   }
  // };

  // ── Filtrado avanzado ────────────────────────────────────────────
  const getFileCategory = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) return 'document';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet';
    if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'flac', 'aac'].includes(ext)) return 'audio';
    return 'other';
  };

  const isWithinDateRange = (dateStr: string, range: string): boolean => {
    if (range === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    
    switch (range) {
      case 'today': return diffDays <= 1;
      case 'week': return diffDays <= 7;
      case 'month': return diffDays <= 30;
      case 'year': return date.getFullYear() === now.getFullYear();
      default: return true;
    }
  };

  const filteredFolders = drive.folders.filter(() => {
    if (filterType !== 'all' && filterType !== 'folder') return false;
    return true;
  });

  const filteredFiles = drive.files.filter(file => {
    // Filtro por tipo
    if (filterType !== 'all') {
      if (filterType === 'folder') return false;
      const category = getFileCategory(file.name);
      if (category !== filterType) return false;
    }
    
    // Filtro por fecha
    if (filterDate !== 'all') {
      if (!isWithinDateRange(file.lastModified, filterDate)) return false;
    }
    
    return true;
  });

  // ── Empty state ──────────────────────────────────────────────────
  const isEmpty = !drive.loading && filteredFolders.length === 0 && filteredFiles.length === 0;
  const hasActiveFilters = filterType !== 'all' || filterDate !== 'all';

  return (
    <div
      className="d-flex flex-column vh-100"
      style={{ background: "#f8fafc", color: "#1e293b" }}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => {
        setCtx(null);
        setBgCtx(null);
        drive.clearSelection();
      }}
      onContextMenu={(e) => {
        // Si no hay target específico, mostrar menú de fondo
        const target = e.target as HTMLElement;
        if (target.closest('[data-item]') || target.closest('.feebbo-table')) {
          return; // Dejar que los items manejen su propio context menu
        }
        e.preventDefault();
        setBgCtx({ x: e.clientX, y: e.clientY });
        setCtx(null);
      }}
    >
      {/* ── DRAG OVERLAY ─────────────────────────────────────────── */}
      {dragOver && (
        <div className="feebbo-drag-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1100, pointerEvents: "none" }}>
          <div className="text-center">
            <i className="bi bi-cloud-upload d-block mb-2" style={{ fontSize: 64, color: "#2563eb" }} />
            <h4 className="fw-semibold" style={{ color: "#1e293b" }}>
              Suelta para subir
            </h4>
          </div>
        </div>
      )}

      {/* ── NAVBAR ───────────────────────────────────────────────── */}
      <nav className="navbar navbar-expand feebbo-navbar px-3 py-2" style={{ zIndex: 100 }}>
        <span className="navbar-brand d-flex align-items-center gap-2 mb-0">
          <span style={{ fontSize: 18, color: "#2563eb", fontWeight: 700 }}>■</span>
          <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 16 }}>
            Feebbo<span style={{ color: "#2563eb" }}>Drive</span>
          </span>
        </span>

        {/* Search */}
        <div className="mx-auto d-flex align-items-center gap-2">
          <div className="input-group input-group-sm" style={{ width: 340 }}>
            <span className="input-group-text feebbo-input" style={{ borderRight: "none" }}>
              <i className="bi bi-search" style={{ color: "#94a3b8" }} />
            </span>
            <input ref={searchInputRef} type="text" className="form-control feebbo-input" style={{ borderLeft: "none" }} placeholder="Buscar archivos… (Ctrl+F)" value={drive.search} onChange={(e) => drive.setSearch(e.target.value)} />
            {drive.search && (
              <button className="btn btn-feebbo-outline btn-sm" onClick={() => drive.setSearch("")}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
          <button
            className="btn btn-sm btn-feebbo-outline"
            onClick={() => navigate("/links")}
            title="Gestionar enlaces públicos"
          >
            <i className="bi bi-link-45deg me-1" />
            Enlaces
          </button>
        </div>

        {/* User */}
        <div className="ms-auto d-flex align-items-center gap-2">
          <div className="feebbo-userchip d-flex align-items-center gap-2 px-2 py-1">
            <div className="feebbo-avatar" aria-hidden="true">
              {userInitial}
            </div>
            <span className="small text-truncate d-none d-md-inline" style={{ maxWidth: 220 }}>
              {userLabel}
            </span>
          </div>
          <button className="btn btn-sm btn-feebbo-outline" onClick={handleSignOut}>
            <i className="bi bi-box-arrow-right me-1" />
            Salir
          </button>
        </div>
      </nav>

      {/* ── BODY ─────────────────────────────────────────────────── */}
      <div className="d-flex flex-grow-1 overflow-hidden">
        {/* ── SIDEBAR ──────────────────────────────────────────── */}
        <aside 
          style={{ 
            width: sidebarWidth, 
            minWidth: 200,
            maxWidth: 500,
            flexShrink: 0,
            borderRight: '1px solid #e2e8f0', 
            overflowY: 'auto',
            background: '#ffffff',
            position: 'relative'
          }}
        >
          <DirectoryTree
            roots={tree.roots}
            currentPrefix={drive.prefix}
            expanded={tree.expanded}
            onToggle={tree.toggle}
            onNavigate={drive.navigate}
            onDrop={(targetPath, sourcePaths) => {
              const items: api.MoveItem[] = sourcePaths.map(source => {
                const isFolder = source.endsWith('/');
                const cleanSource = source.replace(/^\/+/, '').replace(/\/$/, '');
                const fileName = cleanSource.split('/').pop() || '';
                const destDir = targetPath ? targetPath.replace(/^\/+/, '').replace(/\/$/, '') : '';
                const destPath = destDir ? `${destDir}/${fileName}` : fileName;
                return {
                  type: (isFolder ? 'folder' : 'file') as 'file' | 'folder',
                  path: isFolder ? `${cleanSource}/` : cleanSource,
                  destination: isFolder ? `${destPath}/` : destPath
                };
              });
              executeMoveWithToast(items);
            }}
            onNewFolder={() => setShowNewFolder(true)}
            onContextMenu={(e, path, name) => {
              setCtx({
                x: e.clientX,
                y: e.clientY,
                item: { path: path.endsWith('/') ? path : `${path}/`, name } as DriveFolder,
                type: 'folder'
              });
              setBgCtx(null);
            }}
          />
          {/* Handle de resize */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              background: 'transparent',
              zIndex: 10
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              isResizingRef.current = true;
              const startX = e.clientX;
              const startWidth = sidebarWidth;
              
              const onMouseMove = (ev: MouseEvent) => {
                if (!isResizingRef.current) return;
                const newWidth = Math.min(500, Math.max(200, startWidth + (ev.clientX - startX)));
                setSidebarWidth(newWidth);
              };
              
              const onMouseUp = () => {
                isResizingRef.current = false;
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
              };
              
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
              document.addEventListener('mousemove', onMouseMove);
              document.addEventListener('mouseup', onMouseUp);
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#2563eb'; }}
            onMouseLeave={(e) => { if (!isResizingRef.current) (e.target as HTMLElement).style.background = 'transparent'; }}
          />
        </aside>

        {/* ── MAIN CONTENT ─────────────────────────────────────── */}
        <main className="d-flex flex-column flex-grow-1 overflow-hidden" style={{ maxWidth: '100%' }}>
          {/* Toolbar */}
          <div className="px-4 py-2 d-flex align-items-center gap-3" style={{ flexShrink: 0, background: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
            {/* Breadcrumbs */}
            <div className="flex-grow-1 overflow-hidden">
              <Breadcrumbs breadcrumbs={drive.breadcrumbs} basePath={drive.basePath} onNavigate={drive.navigate} />
            </div>
            
            {/* Filtros avanzados */}
            <div className="d-flex gap-2">
              {/* Filtro por tipo */}
              <select 
                className="form-select form-select-sm" 
                style={{ width: 'auto', minWidth: 120 }}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="folder">Carpetas</option>
                <option value="image">Imágenes</option>
                <option value="pdf">PDF</option>
                <option value="document">Documentos</option>
                <option value="spreadsheet">Hojas de cálculo</option>
                <option value="video">Videos</option>
                <option value="audio">Audio</option>
              </select>
              
              {/* Filtro por fecha */}
              <select 
                className="form-select form-select-sm" 
                style={{ width: 'auto', minWidth: 140 }}
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              >
                <option value="all">Cualquier fecha</option>
                <option value="today">Últimas 24 horas</option>
                <option value="week">Últimos 7 días</option>
                <option value="month">Últimos 30 días</option>
                <option value="year">Este año</option>
              </select>
            </div>

            {/* Selection actions */}
            {drive.selected.size > 0 && (
              <div className="d-flex align-items-center gap-2">
                <span className="badge badge-feebbo">{drive.selected.size} sel.</span>
                <button className="btn btn-sm btn-feebbo-outline" onClick={drive.downloadSelected} title="Descargar seleccionados">
                  <i className="bi bi-download" />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    const items = [...drive.allFolders.filter((f) => drive.selected.has(f.path)), ...drive.allFiles.filter((f) => drive.selected.has(f.path))];
                    setDeleteItems(items);
                  }}
                  title="Eliminar seleccionados"
                >
                  <i className="bi bi-trash" />
                </button>
              </div>
            )}

            {/* View & actions */}
            {/* (removed) Nueva carpeta / Mover buttons per user request */}

            <div className="btn-group btn-group-sm" role="group">
              <button className="btn btn-feebbo-outline" onClick={() => setViewMode("grid")} title="Vista en cuadrícula" style={{ opacity: viewMode === "grid" ? 1 : 0.5 }}>
                <i className="bi bi-grid" />
              </button>
              <button className="btn btn-feebbo-outline" onClick={() => setViewMode("list")} title="Vista en lista" style={{ opacity: viewMode === "list" ? 1 : 0.5 }}>
                <i className="bi bi-list-ul" />
              </button>
            </div>

            <button className="btn btn-sm btn-feebbo-outline" onClick={() => afterMutation()} title="Refrescar">
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>

          {/* File area */}
          <div className="flex-grow-1 overflow-auto pt-4 px-4" style={{ background: "#f8fafc" }} onClick={(e) => e.stopPropagation()}>
            {drive.loading && (
              <LoadingIndicator onRetry={() => drive.load(drive.prefix)} />
            )}

            {!drive.loading && drive.error && (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <div className="text-center" style={{ maxWidth: 500 }}>
                  <i className="bi bi-shield-lock mb-3" style={{ fontSize: 64, color: "#ef4444" }} />
                  <h5 className="fw-semibold mb-2" style={{ color: "#1e293b" }}>
                    {drive.error.includes('sin permisos') ? 'Acceso Denegado' : 'Error al Cargar'}
                  </h5>
                  <p className="mb-3" style={{ color: "#64748b" }}>
                    {drive.error}
                  </p>
                  {!drive.error.includes('sin permisos') && (
                    <button className="btn btn-feebbo" onClick={() => drive.load(drive.prefix)}>
                      <i className="bi bi-arrow-clockwise me-2" />
                      Reintentar
                    </button>
                  )}
                  {drive.error.includes('sin permisos') && (
                    <div className="alert alert-warning mt-3" style={{ textAlign: 'left' }}>
                      <strong>¿Qué hacer?</strong>
                      <ul className="mb-0 mt-2">
                        <li>Contacta con el administrador del sistema</li>
                        <li>Solicita que te agreguen a la lista de usuarios autorizados</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!drive.loading && !drive.error && isEmpty && (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <i className="bi bi-folder2-open mb-3" style={{ fontSize: 64, color: "#cbd5e1" }} />
                <h5 className="fw-semibold" style={{ color: "#1e293b" }}>
                  Esta carpeta está vacía
                </h5>
                <p className="small mb-4" style={{ color: "#64748b" }}>
                  Arrastra archivos aquí o usa el botón de subir
                </p>
                <div className="d-flex gap-2">
                  <button className="btn btn-feebbo" onClick={() => fileInputRef.current?.click()}>
                    <i className="bi bi-cloud-upload me-2" />
                    Subir archivos
                  </button>
                </div>
              </div>
            )}

            {!drive.loading && !drive.error && isEmpty && hasActiveFilters && (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <i className="bi bi-funnel mb-3" style={{ fontSize: 64, color: '#cbd5e1' }} />
                <h5 className="fw-semibold" style={{ color: '#1e293b' }}>
                  No hay resultados
                </h5>
                <p className="small mb-4" style={{ color: '#64748b' }}>
                  Intenta ajustar los filtros
                </p>
                <button 
                  className="btn btn-feebbo-outline" 
                  onClick={() => {
                    setFilterType('all');
                    setFilterDate('all');
                  }}
                >
                  <i className="bi bi-x-circle me-2" />
                  Limpiar filtros
                </button>
              </div>
            )}

            {!drive.loading && !drive.error && !isEmpty && (
              <FileGrid
                folders={filteredFolders}
                files={filteredFiles}
                selected={drive.selected}
                viewMode={viewMode}
                onFolderOpen={(f) => drive.navigate(f.path)}
                onFileDownload={drive.downloadFile}
                onFileOpen={drive.openFile}
                onSelect={(path, multi) => drive.toggleSelect(path, multi)}
                onContextMenu={(e, item, type) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCtx({ x: e.clientX, y: e.clientY, item, type });
                }}
                onMoveItems={(targetFolderPath, sourcePaths) => {
                  const items: api.MoveItem[] = sourcePaths.map(source => {
                    const isFolder = source.endsWith('/');
                    const cleanSource = source.replace(/^\/+/, '').replace(/\/$/, '');
                    const fileName = cleanSource.split('/').pop() || '';
                    const destDir = targetFolderPath ? targetFolderPath.replace(/^\/+/, '').replace(/\/$/, '') : '';
                    const destPath = destDir ? `${destDir}/${fileName}` : fileName;
                    return {
                      type: (isFolder ? 'folder' : 'file') as 'file' | 'folder',
                      path: isFolder ? `${cleanSource}/` : cleanSource,
                      destination: isFolder ? `${destPath}/` : destPath
                    };
                  });
                  drive.clearSelection();
                  executeMoveWithToast(items);
                }}
              />
            )}
          </div>
        </main>
      </div>

      {/* ── HIDDEN FILE INPUT ────────────────────────────────────── */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) upload.upload(files);
          e.target.value = "";
        }}
      />

      {/* ── MODALS ───────────────────────────────────────────────── */}
      {showNewFolder && (
        <NewFolderModal
          prefix={newFolderPrefix || drive.prefix}
          onClose={() => {
            setShowNewFolder(false);
            setNewFolderPrefix('');
          }}
          onDone={() => {
            const targetPrefix = newFolderPrefix || drive.prefix;
            setShowNewFolder(false);
            afterMutation([targetPrefix]);
            tree.refresh({ paths: [targetPrefix] });
            setNewFolderPrefix('');
          }}
        />
      )}

      {renameTarget && (
        <RenameModal
          file={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={handleRenameDone}
        />
      )}

      <DeleteModal
        show={deleteItems.length > 0}
        items={deleteItems}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!deleting) {
            setDeleteItems([]);
          }
        }}
        deleting={deleting}
      />

      <OverwriteModal
        show={!!upload.overwritePrompt}
        fileName={upload.overwritePrompt?.fileName || ''}
        onOverwrite={upload.onOverwrite}
        onOverwriteAll={upload.onOverwriteAll}
        onSkip={upload.onSkip}
        onCancel={upload.onCancelOverwrite}
      />

      <MoveModal
        show={moveItems.length > 0}
        itemCount={moveItems.length}
        roots={tree.roots}
        expanded={tree.expanded}
        currentPath={drive.prefix}
        onToggle={() => {}}
        onConfirm={handleMoveConfirm}
        onCancel={() => setMoveItems([])}
      />

      {/* ── CONTEXT MENU ─────────────────────────────────────────── */}
      {ctx && (() => {
        const hasSelection = drive.selected.size > 0;
        const clickedInSelection = hasSelection && drive.selected.has(ctx.item.path);
        const selectedFiles = drive.allFiles.filter(f => drive.selected.has(f.path));
        const shouldDownloadMultiple = ctx.type === "file" && clickedInSelection && selectedFiles.length > 1;
        
        return (
          <ContextMenu
            x={ctx.x}
            y={ctx.y}
            item={ctx.item}
            type={ctx.type}
            onClose={() => setCtx(null)}
            selectedCount={shouldDownloadMultiple ? selectedFiles.length : undefined}
            onDownload={ctx.type === "file" ? () => {
              if (shouldDownloadMultiple) {
                // Descargar múltiples archivos usando ZIP (igual que el botón de arriba)
                drive.downloadSelected();
              } else {
                // Descargar solo el archivo clicado
                drive.downloadFile(ctx.item.path);
              }
            } : undefined}
          onRename={() => setRenameTarget(ctx.item)}
          onMove={() => {
            const hasSelection = drive.selected.size > 0;
            const clickedInSelection = hasSelection && drive.selected.has(ctx.item.path);
            if (clickedInSelection) {
              const selectedItems = [
                ...drive.allFolders.filter(f => drive.selected.has(f.path)),
                ...drive.allFiles.filter(f => drive.selected.has(f.path)),
              ];
              setMoveItems(selectedItems);
            } else {
              setMoveItems([ctx.item]);
            }
            setCtx(null); // Cerrar el menú después de configurar los items
          }}
          onDelete={() => {
            // Si el item clicado está dentro de la selección actual, borrar todos los seleccionados
            if (drive.selected.size > 0 && drive.selected.has(ctx.item.path)) {
              const items = [
                ...drive.allFolders.filter((f) => drive.selected.has(f.path)),
                ...drive.allFiles.filter((f) => drive.selected.has(f.path)),
              ];
              setDeleteItems(items);
            } else {
              setDeleteItems([ctx.item]);
            }
            setCtx(null);
          }}
          onDownloadZip={ctx.type === "folder" ? () => handleDownloadZip(ctx.item.path) : undefined}
          onShare={ctx.type === "file" ? () => { setShareTarget(ctx.item as DriveFile); setCtx(null); } : undefined}
          onNewFolder={ctx.type === "folder" ? () => {
            setNewFolderPrefix(ctx.item.path.replace(/\/$/, ''));
            setShowNewFolder(true);
            setCtx(null);
          } : undefined}
        />
        );
      })()}

      {/* ── SHARE LINK MODAL ─────────────────────────────────────── */}
      {shareTarget && (
        <ShareLinkModal
          file={shareTarget}
          onClose={() => setShareTarget(null)}
        />
      )}

      {/* ── BACKGROUND CONTEXT MENU ──────────────────────────────── */}
      {bgCtx && (
        <BackgroundContextMenu
          x={bgCtx.x}
          y={bgCtx.y}
          onClose={() => setBgCtx(null)}
          onNewFolder={() => setShowNewFolder(true)}
          onUpload={() => setShowUploadModal(true)}
        />
      )}

      {/* ── UPLOAD MODAL ─────────────────────────────────────────── */}
      <UploadModal
        show={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setDragOver(false);
          dragCountRef.current = 0;
        }}
        onUpload={(files) => {
          setShowUploadModal(false);
          setDragOver(false);
          dragCountRef.current = 0;
          upload.upload(files);
        }}
      />

      {/* ── BOTÓN FLOTANTE SUBIR ─────────────────────────────────── */}
      <button 
        className="btn btn-feebbo rounded-circle shadow-lg" 
        type="button" 
        onClick={() => setShowUploadModal(true)}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000,
          width: '60px',
          height: '60px',
          fontSize: '24px'
        }}
        title="Subir archivos"
      >
        <i className="bi bi-plus-lg" />
      </button>

      {/* ── UPLOAD TOAST ─────────────────────────────────────────── */}
      <UploadToast 
        items={upload.items} 
        onClose={upload.clear}
        bottomOffset={
          (drive.downloadingZip || downloadingContextZip ? 120 : 24) +
          (movingItems ? 120 : 0)
        }
      />

      {/* ── DOWNLOAD ZIP TOAST (bottom-right) ─────────────────────── */}
      {(drive.downloadingZip || downloadingContextZip) && (
        <div
          className="shadow-lg"
          style={{
            position: 'fixed',
            right: '1.25rem',
            bottom: '1.25rem',
            zIndex: 1100,
            width: 320,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            overflow: 'hidden'
          }}
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {drive.zipPhase === 'descargando' ? (
              <i className="bi bi-check-circle-fill" style={{ fontSize: 18, color: '#22c55e' }} />
            ) : (
              <span className="spinner-border spinner-border-sm" style={{ color: '#2563eb' }} />
            )}
            <strong style={{ color: '#0f172a', fontSize: 14 }}>
              {drive.zipPhase === 'descargando' ? 'ZIP descargado' : 'Generando ZIP'}
            </strong>
          </div>
          <div className="px-3 py-3">
            {/* Barra de progreso (solo cuando tenemos progreso desde drive) */}
            {drive.downloadingZip ? (
              <>
                <div className="progress" style={{ height: 8, borderRadius: 4, background: '#e2e8f0' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${drive.zipProgress || 0}%`,
                      background: drive.zipPhase === 'descargando' ? '#22c55e' : '#2563eb',
                      transition: 'width 0.5s ease'
                    }}
                  />
                </div>
                <div className="d-flex justify-content-between mt-1">
                  <span className="small" style={{ color: '#64748b' }}>
                    {drive.zipPhase === 'compressing' || drive.zipPhase === 'comprimiendo' ? 'Comprimiendo archivos'
                      : drive.zipPhase === 'uploading' ? 'Preparando ZIP'
                      : drive.zipPhase === 'descargando' ? 'Descarga completada'
                      : drive.zipPhase === 'iniciando' ? 'Iniciando'
                      : 'Preparando'}
                  </span>
                  <span className="small fw-semibold" style={{ color: drive.zipPhase === 'descargando' ? '#22c55e' : '#2563eb' }}>
                    {drive.zipProgress || 0}%
                  </span>
                </div>
              </>
            ) : (
              // Context ZIP (sin progreso detallado): barra indeterminada sutil
              <div className="progress" style={{ height: 8, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
                <div
                  style={{
                    width: '40%', height: '100%', background: '#2563eb',
                    animation: 'zip-indeterminate 1.2s infinite', borderRadius: 4
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MOVE TOAST (bottom-right, stacked above ZIP toast) ────── */}
      {movingItems && (
        <div
          className="shadow-lg"
          style={{
            position: 'fixed',
            right: '1.25rem',
            bottom: (drive.downloadingZip || downloadingContextZip) ? '8rem' : '1.25rem',
            zIndex: 1100,
            width: 320,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            overflow: 'hidden',
            transition: 'bottom 0.3s ease'
          }}
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {movePhase === 'completado' ? (
              <i className="bi bi-check-circle-fill" style={{ fontSize: 18, color: '#22c55e' }} />
            ) : movePhase === 'error' || movePhase === 'completado con errores' ? (
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 18, color: '#f59e0b' }} />
            ) : (
              <span className="spinner-border spinner-border-sm" style={{ color: '#2563eb' }} />
            )}
            <strong style={{ color: '#0f172a', fontSize: 14 }}>
              {movePhase === 'completado' ? 'Movido correctamente'
                : movePhase === 'error' ? 'Error al mover'
                : movePhase === 'completado con errores' ? 'Movido con errores'
                : 'Moviendo archivos'}
            </strong>
          </div>
          <div className="px-3 py-3">
            {moveProgress > 0 && moveProgress < 100 ? (
              <>
                <div className="progress" style={{ height: 8, borderRadius: 4, background: '#e2e8f0' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${moveProgress}%`,
                      background: '#2563eb',
                      transition: 'width 0.5s ease'
                    }}
                  />
                </div>
                <div className="d-flex justify-content-between mt-1">
                  <span className="small" style={{ color: '#64748b' }}>
                    {movePhase === 'compressing' || movePhase === 'comprimiendo' ? 'Procesando'
                      : movePhase === 'iniciando' ? 'Iniciando'
                      : 'Moviendo'}
                  </span>
                  <span className="small fw-semibold" style={{ color: '#2563eb' }}>
                    {moveProgress}%
                  </span>
                </div>
              </>
            ) : moveProgress >= 100 ? (
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-lg" style={{ color: '#22c55e' }} />
                <span className="small" style={{ color: '#64748b' }}>Operación completada</span>
              </div>
            ) : (
              <div className="progress" style={{ height: 8, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
                <div
                  style={{
                    width: '40%', height: '100%', background: '#2563eb',
                    animation: 'move-indeterminate 1.2s infinite', borderRadius: 4
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <ErrorModal
          title={errorModal.title}
          message={errorModal.message}
          variant={errorModal.variant}
          onClose={() => setErrorModal(null)}
        />
      )}

      {/* Animación simple para la barra indeterminada */}
      <style>
        {`
          @keyframes zip-indeterminate { 0% { transform: translateX(-100%); } 50% { transform: translateX(60%); } 100% { transform: translateX(160%); } }
          @keyframes move-indeterminate { 0% { transform: translateX(-100%); } 50% { transform: translateX(60%); } 100% { transform: translateX(160%); } }
        `}
      </style>
    </div>
  );
}
